import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../lib/browserStorage';

const LOCAL_STORAGE_KEY = 'app-theme-choice';
const THEME_OPTIONS = ['light', 'dark', 'system'];

const getSystemPreference = () =>
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

const ThemeContext = createContext({
  userChoice: 'system',
  effectiveTheme: 'light',
  cycleTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [userChoice, setUserChoice] = useState(() => {
    const savedChoice = getLocalStorageItem(LOCAL_STORAGE_KEY);
    return THEME_OPTIONS.includes(savedChoice) ? savedChoice : 'system';
  });
  const [systemTheme, setSystemTheme] = useState(getSystemPreference);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    try {
      mediaQuery.addEventListener('change', handleChange);
    } catch {
      return undefined;
    }

    return () => {
      try {
        mediaQuery.removeEventListener('change', handleChange);
      } catch {
        /* ignore unsupported cleanup */
      }
    };
  }, []);

  const effectiveTheme = useMemo(() => {
    return userChoice === 'system' ? systemTheme : userChoice;
  }, [userChoice, systemTheme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveTheme);
    setLocalStorageItem(LOCAL_STORAGE_KEY, userChoice);
  }, [userChoice, effectiveTheme]);

  const cycleTheme = useCallback(() => {
    setUserChoice((prevChoice) => {
      if (prevChoice === 'system') return 'light';
      if (prevChoice === 'light') return 'dark';
      return 'system';
    });
  }, []);

  const value = useMemo(
    () => ({
      userChoice,
      effectiveTheme,
      cycleTheme,
    }),
    [userChoice, effectiveTheme, cycleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
export default ThemeContext;
